/**
 * 未対応pt カードの右に並ぶ 1 枚のカードを選ぶ分岐。
 *
 * 2 種類ある:
 *  - **当月pt 内訳カード** (`MemberPtBreakdownCard`) — 要件定義pt / 開発pt の横棒。
 *    **PDG エンジニア**のとき出す
 *  - **基本情報カード** (`MemberInfoCard`) — nickname / GitHub名 / 30d メトリクス / 前月戦闘力比。
 *    それ以外（ACE エンジニア / IDT designer / PMO / 事業部）の既定
 *
 * **どちらを出すかはメンバー本人の属性だけで決まり、どの画面から見たかには依らない。**
 * my-page（本人）とエンジニアボードのメンバー詳細 (`/members/$email`、他人が見る) は同じ
 * `MemberDetailContent` を使うが、要件定義pt は「その人の当月ptの内訳」であって本人にだけ
 * 意味がある数字ではないため、見る人によって中身を変える理由が無い。リーダーが看板から
 * 入ったときに本人と違うものが出ると、1on1 で同じ画面を見ながら話せなくなる。
 *
 * ACE を外すのは要件定義工程が PDG 側にしか無いため（内訳が常に 要件定義 0pt になる）。
 *
 * `MemberDetailContent` から切り出したのは、同じ 1 枠に 2 種類のカードを出す判断を
 * 本体の JSX に混ぜると、親の責務（どのブロックを縦に並べるか）とカードの選び方が
 * 同じ場所に居ることになるため。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 未対応pt カードの隣に出すカードを選ぶ（PDG エンジニアは当月pt 内訳カード、それ以外は基本情報カード）。閲覧者ではなく対象メンバーの属性で決める
 * @graph-connects isPdgEngineerByTeam [calls] PDG エンジニア判定（combat-power-scope の SSOT）
 * @graph-connects MemberInfoCard [calls] 基本情報カード（nickname / GitHub名 / 30d メトリクス）
 * @graph-connects MemberPtBreakdownCard [calls] 当月pt 内訳カード（要件定義pt / 開発pt）
 */
import { useTranslation } from 'react-i18next';
import { isPdgEngineerByTeam } from '@cortex/domain-product-innovation-lab-combat-power-scope';
import { MemberInfoCard } from './MemberInfoCard.js';
import { MemberPtBreakdownCard } from './MemberPtBreakdownCard.js';
import type { MemberDetail } from '../../types/api.js';

/** @graph-connects none */
interface MemberOverviewCardProps {
  /** 対象メンバー */
  member: MemberDetail;
  /** 前月戦闘力比（戦闘力履歴 API 由来。未取得時は省略） */
  combat?: { prevMonthRatio: number | null };
}

/** @graph-connects none */
export function MemberOverviewCard({ member, combat }: MemberOverviewCardProps) {
  const { t } = useTranslation();

  if (isPdgEngineerByTeam({ name: member.nickName, team: member.teamName })) {
    return <MemberPtBreakdownCard email={member.email} />;
  }

  return (
    <MemberInfoCard
      member={member}
      labels={{
        nickName: t('members.fields.nickName'),
        githubName: t('members.fields.githubName'),
        points30d: t('members.fields.points30d'),
        releaseCount30d: t('members.fields.releaseCount30d'),
        prevMonthRatio: t('members.combatPower.prevMonthRatio'),
      }}
      combat={combat}
    />
  );
}
